import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { Columns3, Plus, Rows3, Shirt, UserCheck } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  UNIFORM_ART_STATUS_VALUES,
  UNIFORM_LOGISTICS_VALUES,
  UNIFORM_PAYMENT_MARK_VALUES,
  UNIFORM_STAGE_VALUES,
  type ProductionOrderSummaryType,
  type ProductionWorkloadType,
} from '@aeris/contracts'
import {
  ActiveFilters,
  Button,
  ColumnChooser,
  EmptyState,
  FilterableHeaderCell,
  InlineAlert,
  ListToolbar,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  ResultCount,
  RowOpener,
  SearchInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  type ActiveFilterChip,
  type ColumnFilterSpec,
  type ColumnFilterValue,
} from '@aeris/ui'
import { canWork } from '@aeris/uniforms'

import { ProductionBoard } from '../components/production-board.tsx'
import { ProductionStageControl } from '../components/production-stage-control.tsx'
import { SalesDocumentForm } from '../components/sales-document-form.tsx'
import { UniformOrderSheet } from '../components/uniform-order-sheet.tsx'
import {
  fetchProductionQueue,
  fetchUniformMembers,
  fetchUniformFabrics,
  fetchUniformPrinters,
} from '../lib/api/uniforms.ts'
import { ApiError, transitionSalesDocument } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { enumValue } from '../lib/column-filters.ts'
import {
  DEFAULT_PRODUCTION_COLUMNS,
  PRODUCTION_COLUMNS,
  dayText,
  type ProductionColumn,
  type ProductionFilterKey,
} from '../lib/production-columns.tsx'
import {
  ART_STATUS_META,
  LOGISTICS_LABELS,
  PAYMENT_MARK_META,
  STAGE_META,
} from '../lib/production-stage-meta.tsx'
import { documentRef } from '../lib/sales-labels.ts'
import {
  availableColumns,
  shownColumnsOf,
  toggledColumns,
} from '../lib/table-columns.ts'
import { useDebounced } from '../lib/use-debounced.ts'
import { usePreference } from '../lib/use-preference.ts'
import { sortKey, useTableSort } from '../lib/use-table-sort.ts'
import { useUniformViewer } from '../lib/use-uniform-viewer.ts'

/**
 * A fila de produção — F170, a primeira tela de vertical do Aeris.
 *
 * Aposenta a planilha "FILA DE PRODUÇÃO" da 4Play: as mesmas colunas, na mesma
 * ordem, com a etapa trocada na própria linha. O que muda é o que a planilha
 * não fazia — o pedido É o pedido de vendas (valor, cliente, número vêm de lá),
 * a grade soma as peças sozinha, o atraso aparece em vermelho, e a impressora
 * fica gravada para quando o cliente pedir de novo.
 *
 * ## Tabela ou quadro (F171)
 *
 * A mesma fila em duas caras: a tabela da planilha, e o quadro com as etapas
 * em colunas, que a pessoa escolhe e o sistema lembra. "Minha fila" recorta o
 * que as funções de quem está olhando têm para fazer, e quem gerencia vê a
 * carga de cada designer e de cada atendente.
 *
 * ## A fila é o que ainda não saiu
 *
 * Pedido confirmado, com ou sem ficha. O pedido sai da fila quando é despachado
 * ou retirado pelo fluxo de sempre (F159). Rascunhos ficam num filtro próprio,
 * para que um "Novo pedido" abandonado não suje a fila.
 */

const PAGE_SIZE = 50

/** O quadro pede a fila inteira de uma vez: são as colunas, não páginas. */
const BOARD_SIZE = 500

type FilterState = Readonly<
  Partial<Record<Exclude<ProductionFilterKey, 'dispatch'>, string>>
> & {
  readonly dispatchFrom?: string
  readonly dispatchTo?: string
}

/** O mês da planilha — "SET/26" —, como faixa de datas de despacho. */
const monthRange = (
  offset: number
): { from: string; to: string; label: string } => {
  const now = new Date()
  const first = new Date(now.getFullYear(), now.getMonth() + offset, 1)
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0)
  const day = (date: Date): string =>
    `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  return {
    from: day(first),
    to: day(last),
    label: first
      .toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
      .replace('.', '')
      .toUpperCase(),
  }
}

export const ProducaoPage = (): ReactNode => {
  const { allows } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { pedido } = useSearch({ from: '/shell/producao' })

  const { actor } = useUniformViewer()
  const canWrite = allows('uniforms.write')
  const canCreate =
    canWrite && allows('sales.write') && canWork(actor, 'atendimento')

  const viewPreference = usePreference('production.view', { view: 'table' })
  const board = viewPreference.value.view === 'board'

  const [search, setSearch] = useState('')
  const [mine, setMine] = useState(false)
  const [drafts, setDrafts] = useState(false)
  const [lateOnly, setLateOnly] = useState(false)
  const [filters, setFilters] = useState<FilterState>({})
  const [page, setPage] = useState(1)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sort = useTableSort<NonNullable<ProductionColumn['sortKey']>>(
    'dispatchDate',
    'asc',
    () => {
      setPage(1)
    }
  )
  const debouncedSearch = useDebounced(search)

  const openOrder = (id: string | null): void => {
    void navigate({
      to: '/producao',
      search: id === null ? {} : { pedido: id },
      replace: id === null,
    })
  }

  const columnsPreference = usePreference('table.production.columns', {
    visible: [...DEFAULT_PRODUCTION_COLUMNS],
  })
  const offeredColumns = availableColumns(PRODUCTION_COLUMNS, allows)
  const shownColumns = shownColumnsOf(
    offeredColumns,
    columnsPreference.value.visible
  )

  const people = useQuery({
    queryKey: ['uniform-members'],
    queryFn: async ({ signal }) => await fetchUniformMembers(signal),
  })
  const fabrics = useQuery({
    queryKey: ['uniform-fabrics'],
    queryFn: ({ signal }) => fetchUniformFabrics(signal),
    staleTime: 60_000,
  })
  const printers = useQuery({
    queryKey: ['uniform-printers', 'all'],
    queryFn: async ({ signal }) =>
      await fetchUniformPrinters(undefined, signal),
  })

  const query = useQuery({
    queryKey: [
      'production-queue',
      {
        search: debouncedSearch,
        mine,
        board,
        drafts,
        lateOnly,
        filters,
        page,
        ...sortKey(sort),
      },
    ],
    queryFn: async ({ signal }) =>
      await fetchProductionQueue(
        {
          ...(debouncedSearch.trim() === ''
            ? {}
            : { search: debouncedSearch.trim() }),
          ...(drafts ? { drafts: 'true' } : {}),
          ...(mine ? { mine: 'true' } : {}),
          ...(lateOnly ? { late: 'true' } : {}),
          ...(filters.stage === undefined ? {} : { stages: filters.stage }),
          ...(filters.designer === undefined
            ? {}
            : { designerUserIds: filters.designer }),
          ...(filters.salesperson === undefined
            ? {}
            : { salespersonUserIds: filters.salesperson }),
          ...(filters.printer === undefined
            ? {}
            : { printerIds: filters.printer }),
          ...(filters.fabric === undefined
            ? {}
            : { fabricIds: filters.fabric }),
          ...(filters.logistics === undefined
            ? {}
            : { logistics: filters.logistics }),
          ...(filters.paymentMark === undefined
            ? {}
            : { paymentMarks: filters.paymentMark }),
          ...(filters.artStatus === undefined
            ? {}
            : { artStatuses: filters.artStatus }),
          ...(filters.dispatchFrom === undefined
            ? {}
            : { dispatchFrom: filters.dispatchFrom }),
          ...(filters.dispatchTo === undefined
            ? {}
            : { dispatchTo: filters.dispatchTo }),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          sort: sort.sort,
          direction: sort.direction,
          page: board ? 1 : page,
          pageSize: board ? BOARD_SIZE : PAGE_SIZE,
        },
        signal
      ),
    placeholderData: (previous) => previous,
  })

  const items = query.data?.items ?? []
  const total = query.data?.total ?? 0

  const setFilter = (
    key: keyof FilterState,
    value: string | undefined
  ): void => {
    setFilters((current) =>
      Object.fromEntries(
        Object.entries({ ...current, [key]: value ?? '' }).filter(
          ([, entry]) => entry !== ''
        )
      )
    )
    setPage(1)
  }

  /** O que cada coluna oferece no menu do cabeçalho. */
  const specOf = (key: ProductionFilterKey): ColumnFilterSpec => {
    switch (key) {
      case 'stage':
        return {
          kind: 'enum',
          options: UNIFORM_STAGE_VALUES.map((stage) => ({
            value: stage,
            label: STAGE_META[stage].label,
          })),
        }
      case 'designer':
      case 'salesperson':
        return {
          kind: 'enum',
          options: (people.data ?? []).map((person) => ({
            value: person.userId,
            label: person.displayName,
          })),
        }
      case 'printer':
        return {
          kind: 'enum',
          options: (printers.data ?? []).map((printer) => ({
            value: printer.id,
            label: printer.name,
          })),
        }
      case 'fabric':
        return {
          kind: 'enum',
          options: [
            ...(fabrics.data ?? []).map((fabric) => ({
              value: fabric.id,
              label: fabric.name,
            })),
            { value: 'a-definir', label: 'A definir' },
          ],
        }
      case 'logistics':
        return {
          kind: 'enum',
          options: UNIFORM_LOGISTICS_VALUES.map((value) => ({
            value,
            label: LOGISTICS_LABELS[value],
          })),
        }
      case 'paymentMark':
        return {
          kind: 'enum',
          options: UNIFORM_PAYMENT_MARK_VALUES.map((mark) => ({
            value: mark,
            label: PAYMENT_MARK_META[mark].label,
          })),
        }
      case 'artStatus':
        return {
          kind: 'enum',
          options: UNIFORM_ART_STATUS_VALUES.map((status) => ({
            value: status,
            label: ART_STATUS_META[status].label,
          })),
        }
      case 'dispatch':
        return { kind: 'dateRange' }
    }
  }

  const valueOf = (key: ProductionFilterKey): ColumnFilterValue | undefined => {
    if (key === 'dispatch') {
      return filters.dispatchFrom === undefined &&
        filters.dispatchTo === undefined
        ? undefined
        : {
            kind: 'dateRange',
            from: filters.dispatchFrom ?? '',
            to: filters.dispatchTo ?? '',
          }
    }
    const value = filters[key]
    return value === undefined ? undefined : enumValue(value)
  }

  const onFilter = (
    key: ProductionFilterKey,
    next: ColumnFilterValue | null
  ): void => {
    if (key === 'dispatch') {
      setFilters((current) => {
        const rest: Record<string, string | undefined> = { ...current }
        delete rest.dispatchFrom
        delete rest.dispatchTo
        if (next?.kind === 'dateRange') {
          if (next.from !== '') rest.dispatchFrom = next.from
          if (next.to !== '') rest.dispatchTo = next.to
        }
        return rest
      })
      setPage(1)
      return
    }
    setFilter(
      key,
      next?.kind === 'enum' && next.value !== '' ? next.value : undefined
    )
  }

  const labelOf = (key: ProductionFilterKey): string => {
    const value = valueOf(key)
    const spec = specOf(key)
    if (value?.kind === 'enum' && spec.kind === 'enum') {
      return (
        spec.options.find((option) => option.value === value.value)?.label ??
        value.value
      )
    }
    if (value?.kind === 'dateRange') {
      if (value.from !== '' && value.to !== '')
        return `${dayText(value.from)} a ${dayText(value.to)}`
      return value.from !== ''
        ? `desde ${dayText(value.from)}`
        : `até ${dayText(value.to)}`
    }
    return ''
  }

  const chips: ActiveFilterChip[] = PRODUCTION_COLUMNS.flatMap((column) =>
    column.filter === undefined || valueOf(column.filter) === undefined
      ? []
      : [
          {
            id: column.id,
            label: `${column.label}: ${labelOf(column.filter)}`,
            onRemove: () => {
              if (column.filter !== undefined) onFilter(column.filter, null)
            },
          },
        ]
  )

  const clearFilters = (): void => {
    setFilters({})
    setSearch('')
    setLateOnly(false)
    setMine(false)
    setPage(1)
  }

  const month = monthRange(0)
  const nextMonth = monthRange(1)

  /** As props de filtro de um cabeçalho — vazias quando a coluna não filtra. */
  const filterProps = (
    column: ProductionColumn
  ): {
    filter?: ColumnFilterSpec
    value?: ColumnFilterValue
    onFilter?: (next: ColumnFilterValue | null) => void
  } => {
    const key = column.filter
    if (key === undefined) return {}
    const value = valueOf(key)
    return {
      filter: specOf(key),
      onFilter: (next) => {
        onFilter(key, next)
      },
      ...(value === undefined ? {} : { value }),
    }
  }

  const cell = (
    column: ProductionColumn,
    order: ProductionOrderSummaryType
  ): ReactNode => {
    if (column.id === 'stage' && canWrite) {
      return (
        <ProductionStageControl
          order={order}
          label={`Etapa do ${documentRef('order', order.number)}`}
        />
      )
    }
    if (column.id === 'number' || column.id === 'customer') {
      return (
        <RowOpener
          onOpen={() => {
            openOrder(order.id)
          }}
        >
          {column.cell(order)}
        </RowOpener>
      )
    }
    return column.cell(order)
  }

  return (
    <Page>
      <PageHeader
        title="Produção"
        description="Os pedidos em produção: a grade, a etapa de cada um e em qual impressora saiu. O pedido sai da fila quando sai da loja."
        actions={
          canCreate ? (
            <Button
              variant="primary"
              leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                setCreating(true)
              }}
            >
              Novo pedido
            </Button>
          ) : null
        }
      />

      <ListToolbar
        actions={
          <div className="flex items-center gap-2">
            <div
              role="group"
              aria-label="Como ver a fila"
              className="flex rounded-(--radius-md) border border-(--border-subtle)"
            >
              <Button
                variant={board ? 'ghost' : 'secondary'}
                size="sm"
                aria-pressed={!board}
                leadingIcon={<Rows3 aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  viewPreference.save({ view: 'table' })
                }}
              >
                Tabela
              </Button>
              <Button
                variant={board ? 'secondary' : 'ghost'}
                size="sm"
                aria-pressed={board}
                leadingIcon={
                  <Columns3 aria-hidden="true" className="h-4 w-4" />
                }
                onClick={() => {
                  viewPreference.save({ view: 'board' })
                }}
              >
                Quadro
              </Button>
            </div>
            {board ? null : (
              <ColumnChooser
                columns={offeredColumns}
                visible={shownColumns.map((column) => column.id)}
                onToggle={(id) => {
                  columnsPreference.save({
                    visible: toggledColumns(
                      columnsPreference.value.visible,
                      id
                    ),
                  })
                }}
                onReset={() => {
                  columnsPreference.save({
                    visible: [...DEFAULT_PRODUCTION_COLUMNS],
                  })
                }}
              />
            )}
          </div>
        }
      >
        <SearchInput
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            setPage(1)
          }}
          placeholder="Buscar pelo número do pedido ou pelo cliente"
          aria-label="Buscar pedidos na fila"
        />
        {actor.functions.length === 0 ? null : (
          <Button
            variant={mine ? 'secondary' : 'ghost'}
            size="sm"
            aria-pressed={mine}
            leadingIcon={<UserCheck aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              setMine((current) => !current)
              setPage(1)
            }}
          >
            Minha fila
          </Button>
        )}
        <Select
          aria-label="Quais pedidos"
          className="w-56"
          value={drafts ? 'drafts' : lateOnly ? 'late' : 'queue'}
          onChange={(event) => {
            setDrafts(event.target.value === 'drafts')
            setLateOnly(event.target.value === 'late')
            setPage(1)
          }}
        >
          <option value="queue">Na fila</option>
          <option value="late">Atrasados</option>
          <option value="drafts">Rascunhos</option>
        </Select>
        <Button
          variant={filters.dispatchFrom === month.from ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => {
            onFilter('dispatch', {
              kind: 'dateRange',
              from: month.from,
              to: month.to,
            })
          }}
        >
          Despacho em {month.label}
        </Button>
        <Button
          variant={
            filters.dispatchFrom === nextMonth.from ? 'secondary' : 'ghost'
          }
          size="sm"
          onClick={() => {
            onFilter('dispatch', {
              kind: 'dateRange',
              from: nextMonth.from,
              to: nextMonth.to,
            })
          }}
        >
          {nextMonth.label}
        </Button>
      </ListToolbar>

      <ActiveFilters chips={chips} onClearAll={clearFilters} />

      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}

      <div className="flex flex-wrap items-baseline gap-x-6">
        <ResultCount
          page={board ? 1 : page}
          pageSize={board ? BOARD_SIZE : PAGE_SIZE}
          total={total}
          loading={query.isPending}
          none="Nenhum pedido na fila"
        />
        {query.data === undefined || query.data.pieces === 0 ? null : (
          <p className="text-(length:--text-sm) text-(--text-secondary)">
            {query.data.pieces} peças no filtro
          </p>
        )}
      </div>

      {query.data === undefined ? null : (
        <QueueSummary
          byStage={query.data.byStage}
          workload={query.data.workload}
          board={board}
        />
      )}

      {board && total > BOARD_SIZE ? (
        <InlineAlert tone="warning">
          O quadro mostra os {BOARD_SIZE} primeiros pedidos do filtro. Use os
          filtros ou a tabela para ver o resto.
        </InlineAlert>
      ) : null}

      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
        what="a fila de produção"
        isEmpty={items.length === 0}
        filtered={chips.length > 0 || search.trim() !== '' || lateOnly || mine}
        onClearFilters={clearFilters}
        empty={
          <EmptyState
            icon={<Shirt aria-hidden="true" className="h-8 w-8" />}
            title={
              drafts
                ? 'Nenhum rascunho'
                : mine
                  ? 'Nada na sua fila'
                  : 'A fila está vazia'
            }
            description={
              drafts
                ? 'Todo pedido criado aqui foi confirmado.'
                : 'Um pedido confirmado aparece aqui na etapa Arte. "Novo pedido" cria e confirma de uma vez.'
            }
          />
        }
      >
        {board ? (
          <ProductionBoard
            items={items}
            byStage={query.data?.byStage ?? {}}
            onOpen={(id) => {
              openOrder(id)
            }}
          />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {shownColumns.map((column) =>
                    column.sortKey === undefined &&
                    column.filter === undefined ? (
                      <TableHeaderCell
                        key={column.id}
                        numeric={column.numeric === true}
                      >
                        {column.label}
                      </TableHeaderCell>
                    ) : (
                      <FilterableHeaderCell
                        key={column.id}
                        numeric={column.numeric === true}
                        {...(column.sortKey === undefined
                          ? {}
                          : sort.column(column.sortKey))}
                        {...filterProps(column)}
                      >
                        {column.label}
                      </FilterableHeaderCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((order) => (
                  <TableRow
                    key={order.id}
                    onOpen={() => {
                      openOrder(order.id)
                    }}
                  >
                    {shownColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        numeric={column.numeric === true}
                        className={column.className}
                      >
                        {cell(column, order)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </QueryState>

      {board ? null : (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      )}

      {creating ? (
        <SalesDocumentForm
          initialKind="order"
          onClose={() => {
            setCreating(false)
          }}
          onSaved={(document) => {
            /*
             * Cria E confirma: o número existe na hora, como na planilha. Se a
             * confirmação falhar, o pedido fica em Rascunhos — e a mensagem
             * diz isso, em vez de a fila parecer ter perdido o pedido.
             */
            void transitionSalesDocument(document.id, {
              version: document.version,
              to: 'confirmed',
            })
              .then(async () => {
                setError(null)
                await queryClient.invalidateQueries({
                  queryKey: ['production-queue'],
                })
                openOrder(document.id)
              })
              .catch((cause: unknown) => {
                setError(
                  `O pedido foi criado mas não confirmado — está em Rascunhos. ${
                    cause instanceof ApiError ? cause.message : ''
                  }`
                )
              })
          }}
        />
      ) : null}

      {pedido === undefined ? null : (
        <UniformOrderSheet
          orderId={pedido}
          onClose={() => {
            openOrder(null)
          }}
          onOpenOrder={(id) => {
            openOrder(id)
          }}
        />
      )}
    </Page>
  )
}

/**
 * O resumo da fila (F171): quantos pedidos em cada etapa e, para quem
 * gerencia, a carga de cada pessoa.
 *
 * No quadro a contagem por etapa já está no topo de cada coluna; aqui fica só
 * a carga.
 */
const QueueSummary = ({
  byStage,
  workload,
  board,
}: {
  byStage: Readonly<
    Partial<Record<ProductionOrderSummaryType['stage'], number>>
  >
  workload: readonly ProductionWorkloadType[]
  board: boolean
}): ReactNode => {
  const stages = UNIFORM_STAGE_VALUES.filter(
    (stage) => (byStage[stage] ?? 0) > 0
  )
  if ((board || stages.length === 0) && workload.length === 0) return null

  return (
    <div className="flex flex-col gap-1 text-(length:--text-sm) text-(--text-secondary)">
      {board || stages.length === 0 ? null : (
        <p>
          {stages
            .map(
              (stage) =>
                `${STAGE_META[stage].label} ${String(byStage[stage] ?? 0)}`
            )
            .join(' · ')}
        </p>
      )}
      {workload.length === 0 ? null : (
        <p>
          <span className="font-(--weight-medium) text-(--text-primary)">
            Carga:{' '}
          </span>
          {workload
            .map(
              (person) =>
                `${person.as === 'designer' ? 'Designer' : 'Atendente'} ${person.name} ${String(person.count)}${
                  person.late === 0
                    ? ''
                    : ` (${String(person.late)} atrasado${person.late === 1 ? '' : 's'})`
                }`
            )
            .join(' · ')}
        </p>
      )}
    </div>
  )
}
